import React, { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Button,
  Form,
  Input,
  Row,
  Col,
  Empty,
  Space,
  Tooltip,
  Popconfirm,
  Switch,
  Select,
} from 'antd';
import CBInputNumber from '../../components/CBInputNumber';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, GlobalOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import CBModal from '../../components/CBModal';
import PageHeader from '../../components/PageHeader';
const GREEN = '#1a6b3c';

// Langue des documents adresses au bureau et aux compagnies du pays (courriels,
// lettres, PDF). Un organisme peut avoir la sienne : voir l'ecran Organismes.
const LANGUES = [
  { value: 'FR', label: 'Français' },
  { value: 'EN', label: 'English' },
];

export default function Pays() {
  const { t } = useTranslation('parametres');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    axiosInstance.get('/v1/pays')
      .then(r => setItems(r.data?.data || []))
      .catch(() => toast.error(i18n.t('parametres:pays.messages.erreurChargement')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ actif: true, tauxChangeXof: 1, ageRetraite: 60, langue: 'FR' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      codeIso: record.codeIso,
      codeCarteBrune: record.codeCarteBrune,
      libelle: record.libelle,
      smigMensuel: record.smigMensuel,
      monnaie: record.monnaie,
      tauxChangeXof: record.tauxChangeXof,
      ageRetraite: record.ageRetraite,
      actif: record.actif,
      langue: record.langue || 'FR',
    });
    setModalOpen(true);
  };

  const onSubmit = async (vals) => {
    const payload = {
      codeIso: vals.codeIso,
      codeCarteBrune: vals.codeCarteBrune,
      libelle: vals.libelle,
      smigMensuel: vals.smigMensuel,
      monnaie: vals.monnaie,
      tauxChangeXof: vals.tauxChangeXof || 1,
      ageRetraite: vals.ageRetraite || 60,
      actif: vals.actif !== false,
      langue: vals.langue,
    };
    try {
      if (editing) {
        await axiosInstance.put(`/v1/pays/${editing.paysTrackingId}`, payload);
        toast.success(t('pays.messages.paysModifie'));
      } else {
        await axiosInstance.post('/v1/pays', payload);
        toast.success(t('pays.messages.paysCree'));
      }
      form.resetFields();
      setModalOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('pays.messages.erreurSauvegarde'));
    }
  };

  const remove = async (record) => {
    try {
      await axiosInstance.delete(`/v1/pays/${record.paysTrackingId}`);
      toast.success(t('pays.messages.paysSupprime'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('pays.messages.erreurSuppression'));
    }
  };

  const cols = [
    { title: t('pays.colonnes.codeIso'), dataIndex: 'codeIso', key: 'iso', width: 90,
      render: (v) => <Tag color="blue" style={{ borderRadius: 20, fontFamily: 'var(--font-main)', fontWeight: 700 }}>{v}</Tag> },
    { title: t('pays.colonnes.codeCarteBrune'), dataIndex: 'codeCarteBrune', key: 'cb', width: 90,
      render: (v) => <Tag color="green" style={{ borderRadius: 20, fontFamily: 'var(--font-main)', fontWeight: 700 }}>{v}</Tag> },
    { title: t('pays.colonnes.pays'), dataIndex: 'libelle', key: 'lib', render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { title: t('pays.colonnes.smigMensuel'), dataIndex: 'smigMensuel', key: 'smig', width: 160, align: 'right',
      render: (v, r) => (
        <span style={{ fontWeight: 700, color: GREEN }}>
          {new Intl.NumberFormat(localeIntl()).format(v)} {r.monnaie}
        </span>
      ) },
    { title: t('pays.colonnes.monnaie'), dataIndex: 'monnaie', key: 'mon', width: 80, align: 'center' },
    { title: t('pays.colonnes.tauxChange'), dataIndex: 'tauxChangeXof', key: 'tx', width: 140, align: 'right' },
    { title: t('pays.colonnes.ageRetraite'), dataIndex: 'ageRetraite', key: 'ar', width: 110, align: 'center',
      render: (v) => t('pays.colonnes.ageEnAnnees', { age: v }) },
    { title: t('pays.colonnes.langueDocuments'), dataIndex: 'langue', key: 'langue', width: 150, align: 'center',
      render: (v) => (v === 'EN'
        ? <Tag color="geekblue" style={{ borderRadius: 20 }}>English</Tag>
        : <Tag style={{ borderRadius: 20 }}>Français</Tag>) },
    { title: t('pays.colonnes.actif'), dataIndex: 'actif', key: 'act', width: 80, align: 'center',
      render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('pays.colonnes.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('pays.colonnes.non')}</Tag> },
    {
      title: '', key: 'actions', width: 90, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={t('pays.actions.modifier')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} style={{ borderRadius: 6 }} />
          </Tooltip>
          <Popconfirm title={t('pays.actions.supprimerConfirmation')} onConfirm={() => remove(r)} okText={t('pays.actions.oui')} cancelText={t('pays.actions.non')}>
            <Tooltip title={t('pays.actions.supprimer')}>
              <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout title={t('pays.page.titreOnglet')}>
      {/* Aucun filtre sur cet ecran : `items` est la liste complete affichee. */}
      <PageHeader
        titre={t('pays.page.titre')}
        icone={<GlobalOutlined />}
        compte={items.length}
        unite={t('pays.page.unite')}
        unitePluriel={t('pays.page.unitePluriel')}
        actions={(
          <Space>
            <Tooltip title={t('pays.actions.actualiser')}>
              <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 8 }} />
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
              style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {t('pays.actions.ajouter')}
            </Button>
          </Space>
        )}
      />

      <div className="cb-card" style={{ padding: 0 }}>
        <Table className="cb-table" dataSource={items} columns={cols} rowKey="paysTrackingId"
          loading={loading} pagination={{ pageSize: 20 }}
          scroll={{ x: 1050 }}
          locale={{ emptyText: <Empty description={t('pays.page.aucunPays')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />
      </div>

      <CBModal
        title={editing ? t('pays.formulaire.titreModifier') : t('pays.formulaire.titreAjouter')}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditing(null); }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSubmit} style={{ marginTop: 8 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="codeIso" label={t('pays.formulaire.codeIso')} rules={[{ required: true, max: 3 }]}>
                <Input placeholder="TGO" maxLength={3} disabled={!!editing} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="codeCarteBrune" label={t('pays.formulaire.codeCarteBrune')} rules={[{ required: true }]}>
                <Input placeholder="TG" maxLength={5} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="libelle" label={t('pays.formulaire.nomPays')} rules={[{ required: true }]}>
                <Input placeholder="Togo" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="monnaie" label={t('pays.formulaire.monnaie')} rules={[{ required: true }]}>
                <Input placeholder="XOF" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="smigMensuel" label={t('pays.formulaire.smigMensuel')} rules={[{ required: true }]}>
                <CBInputNumber style={{ width: '100%' }} min={0} step={1000}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={v => `${v}`.replace(/\s/g, '')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tauxChangeXof" label={t('pays.formulaire.tauxChange')}>
                <CBInputNumber style={{ width: '100%' }} min={0} step={0.01} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ageRetraite" label={t('pays.formulaire.ageRetraite')}>
                <CBInputNumber style={{ width: '100%' }} min={50} max={75} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="langue" label={t('pays.formulaire.langueDocuments')}
                tooltip={t('pays.formulaire.langueDocumentsAide')}>
                <Select options={LANGUES} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="actif" label={t('pays.formulaire.actif')} valuePropName="checked">
                <Switch checkedChildren={t('pays.formulaire.oui')} unCheckedChildren={t('pays.formulaire.non')} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => { setModalOpen(false); setEditing(null); }}>{t('pays.formulaire.annuler')}</Button>
            <Button type="primary" htmlType="submit" style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {editing ? t('pays.formulaire.modifier') : t('pays.formulaire.creer')}
            </Button>
          </div>
        </Form>
      </CBModal>
    </Layout>
  );
}
